import { appConfig } from '@/config/appConfig';
import { TimeIntervalUnit } from '@/types/TimeIntervalUnit';
import type { ValidationError } from '@/types/ValidationError';

/**
 * Formats a given date into a specified format.
 * @param date - The date to be formatted.
 * @param format - The format to be applied to the date. Defaults to 'D MMM YYYY'.
 * @returns The formatted date as a string.
 */

/**
 * Converts an object of parameters into a query string.
 * @param params - The parameters to convert.
 * @returns The generated query string.
 */
export function toQueryString(params: Record<string, unknown>): string {
    const queryString = Object.keys(params)
        .filter((key) => params[key] !== null && params[key] !== undefined && params[key] !== '' && !Number.isNaN(params[key]))
        .map((key) => `${key}=${params[key]?.toString()}`)
        .join('&');

    return `?${queryString}`;
}

export type AnyObject = Record<string, unknown>;

/**
 * Cleans an object by converting empty strings to null and handling nested objects/arrays.
 * Properties with undefined values are removed.
 *
 * @template T - The type of the object.
 * @param {T} obj - The object to be cleaned.
 * @returns {T | undefined} - The cleaned object or undefined if the input object is undefined.
 *
 * @example
 * const obj = {
 *   name: 'John',
 *   age: 30,
 *   address: '',
 *   email: null,
 *   phone: undefined,
 *   settings: { theme: '', language: 'en' }
 * };
 *
 * const cleanedObj = cleanObject(obj);
 * // Output: { name: 'John', age: 30, address: null, email: null, settings: { theme: null, language: 'en' } }
 */
export function cleanObject<T extends AnyObject>(obj?: T): T | undefined {
    if (!obj) {
        return undefined;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
        const cleanedArray = obj.map((item) => {
            if (typeof item === 'object' && item !== null) {
                return cleanObject(item as AnyObject);
            }
            return item === '' ? null : (item as unknown);
        });
        return cleanedArray as unknown as T;
    }

    const cleanedObj: AnyObject = {};

    Object.entries(obj).forEach(([key, value]) => {
        // Handle nested objects
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            const nestedCleaned = cleanObject(value as AnyObject);
            if (nestedCleaned) {
                cleanedObj[key] = nestedCleaned;
            }
            return;
        }

        // Handle arrays
        if (Array.isArray(value)) {
            const cleanedArray = value.map((item) => {
                if (typeof item === 'object' && item !== null) {
                    return cleanObject(item as AnyObject);
                }
                return item === '' ? null : (item as unknown);
            });
            cleanedObj[key] = cleanedArray;
            return;
        }

        // Handle primitive values
        if (value === '') {
            cleanedObj[key] = null;
        } else if (value !== undefined) {
            cleanedObj[key] = value;
        }
    });

    return cleanedObj as T;
}

/**
 * Retrieves a cryptographic key used for encryption and decryption.
 * @returns A promise that resolves to a CryptoKey object.
 * @example
 * ```typescript
 * const key = await getKey();
 * ```
 */
export async function getKey(): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(appConfig.env.appKey.padEnd(32, ' '));
    const key = await crypto.subtle.importKey('raw', keyData, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    return key;
}

/**
 * Encrypts the given text using AES-GCM encryption algorithm.
 * @param text The text to be encrypted.
 * @returns A promise that resolves to the encrypted string.
 */
export async function encrypt(text: string): Promise<string> {
    const key = await getKey();
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encrypted = await crypto.subtle.encrypt(
        {
            name: 'AES-GCM',
            iv,
        },
        key,
        data
    );

    const buffer = new Uint8Array(encrypted);
    const ivStr = Array.from(iv)
        .map((byte) => String.fromCharCode(byte))
        .join('');
    const dataStr = Array.from(buffer)
        .map((byte) => String.fromCharCode(byte))
        .join('');

    return btoa(ivStr + dataStr);
}

/**
 * Decrypts the given encrypted text using AES-GCM encryption algorithm.
 * @param encryptedText The encrypted text to be decrypted.
 * @returns A promise that resolves to the decrypted text.
 */
export async function decrypt(encryptedText: string): Promise<string> {
    const key = await getKey();
    const encryptedArray = atob(encryptedText)
        .split('')
        .map((character) => character.charCodeAt(0));
    const iv = new Uint8Array(encryptedArray.slice(0, 12));
    const data = new Uint8Array(encryptedArray.slice(12));

    const decrypted = await crypto.subtle.decrypt(
        {
            name: 'AES-GCM',
            iv,
        },
        key,
        data
    );

    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
}

/* export function getValidationError(name: string, validationErrors: ValidationError[]): ValidationError | null {
    return validationErrors.find((err) => err.field === name) ?? null;
} */

export function getValidationError(name: string, validationErrors: ValidationError[] | null): ValidationError | undefined {
    if (!validationErrors) return undefined;

    return validationErrors.find((err) => err.field === name);
}

export function getValidationErrorMessage(name: string, validationErrors: ValidationError[] | null): string | undefined {
    if (!validationErrors) return undefined;
    const validationError = validationErrors.find((err) => err.field === name);

    return validationError?.message;
}

export function convertTimeUnits(value: number, fromUnit: TimeIntervalUnit, toUnit: TimeIntervalUnit): number {
    const timeUnitToDays = {
        [TimeIntervalUnit.Days]: 1,
        [TimeIntervalUnit.Weeks]: 7,
        [TimeIntervalUnit.Months]: 30,
        [TimeIntervalUnit.Years]: 365,
    };

    // Convert to days first, then to target unit
    const days = value * timeUnitToDays[fromUnit as keyof typeof timeUnitToDays];
    return days / timeUnitToDays[toUnit as keyof typeof timeUnitToDays];
}

export function isTimeIntervalValid(
    timeInterval: number,
    timeIntervalUnit: TimeIntervalUnit,
    timeThreshold: number,
    timeThresholdUnit: TimeIntervalUnit
): boolean {
    const intervalInDays = convertTimeUnits(timeInterval, timeIntervalUnit, TimeIntervalUnit.Days);
    const reminderInDays = convertTimeUnits(timeThreshold, timeThresholdUnit, TimeIntervalUnit.Days);

    return reminderInDays < intervalInDays;
}

export function localizeNumbersToThousands(value: number): string {
    if (value < 1000) {
        return value.toString();
    }

    const thousands = value / 1000;
    return `${thousands}K`;
}

export function arePrimitiveArraysEqual<T extends string | number | boolean>(
    firstArray: T[] | undefined | null,
    secondArray: T[] | undefined | null
): boolean {
    if (!firstArray || !secondArray) return false;
    if (firstArray === secondArray) return true;
    if (firstArray.length !== secondArray.length) return false;

    const sortedFirstArray = [...firstArray].sort();
    const sortedSecondArray = [...secondArray].sort();

    for (const [index, value] of sortedFirstArray.entries()) {
        if (value !== sortedSecondArray[index]) return false;
    }

    return true;
}
